import { activateSpan } from './uiOtel';

// Boot-phase tracing helpers for $store.initialize / AppWrapper: the boot is two serialized
// waves ($dictionary gates everything else), and without per-store spans the slow stores are
// invisible in Honeycomb. Identity (company_id/user_id) rides on the tracer resource.

/**
 * Runs fn inside a span and always yields a promise, even when tracing is not initialized
 * (activateSpan returns fn unwrapped in that case), fn returns a plain value, or fn throws
 * synchronously. The async thunk turns a synchronous throw into a rejection so callers'
 * .catch() sees it and activateSpan's finally still ends the span.
 */
export function runWithBootSpan(fn, name, attributes) {
  return activateSpan(async () => fn(), name, attributes)();
}

/**
 * Returns an initStore(store, wave) function producing a `store.initialize <$name>` span per
 * store, named via reverse lookup on the owner ($store) so Honeycomb can group by store.
 */
export function createStoreInitSpanner(owner) {
  const storeNames = new Map(Object.keys(owner).map((key) => [owner[key], key]));
  return (store, wave) => {
    if (!store?.initialize) {
      return Promise.resolve();
    }
    const name = storeNames.get(store) || 'unknown';
    return runWithBootSpan(() => store.initialize(), `store.initialize ${name}`, { store: name, boot_wave: wave });
  };
}

/**
 * Tracks tab visibility across the boot so the boot histogram can separate genuinely slow
 * boots from background-tab throttling: 'visible' (never hidden), 'hidden' (started hidden),
 * 'mixed' (started visible, hidden at some point before appReady).
 */
export function createBootVisibilityTracker(doc = document) {
  let state = doc.visibilityState;
  const onChange = () => {
    if (doc.visibilityState === 'hidden' && state === 'visible') {
      state = 'mixed';
    }
  };
  doc.addEventListener('visibilitychange', onChange);
  return {
    get: () => state,
    stop: () => doc.removeEventListener('visibilitychange', onChange)
  };
}
